"use client";

import React from "react";
import { motion } from "framer-motion";
import { UserCheck, PiggyBank, SlidersHorizontal as Sliders, Zap, ShieldCheck, Sparkles, Check, X, ArrowRight, MessageSquare } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  id: "expertise",
  title: "100% Senior Expertise",
  blushTag: "You Deal Directly With Me",
  consultantPoint: "Every technical audit, strategy document, and SEO campaign is crafted and executed personally by me, Yassine Essaadi.",
  agencyPoint: "Senior directors win your contract, but day-to-day execution is handed off to junior staff or interns.",
  metric: "0% Junior Handoffs",
  icon: UserCheck
}, {
  id: "efficiency",
  title: "High Cost Efficiency",
  blushTag: "Zero Agency Overhead",
  consultantPoint: "100% of your investment goes straight into driving organic growth—not paying for agency commissions, account management layers, or office suites.",
  agencyPoint: "Up to 50% of your retainer covers agency overheads, sales commissions, and internal admin costs.",
  metric: "100% Retainer Value",
  icon: PiggyBank
}, {
  id: "strategy",
  title: "Bespoke & Agile Strategy",
  blushTag: "Tailored Growth Roadmap",
  consultantPoint: "I construct a customized SEO framework designed specifically around your business goals, tech stack, and sector dynamics.",
  agencyPoint: "Standardized assembly-line checklists and cookie-cutter deliverables engineered for agency volume.",
  metric: "100% Tailored Plan",
  icon: Sliders
}, {
  id: "communication",
  title: "Direct & Fast Communication",
  blushTag: "No Account Managers",
  consultantPoint: "You get direct communication with me via Slack or email. Quick answers, immediate tactical turns, and clear feedback without friction.",
  agencyPoint: "Every question is routed through account managers, leading to delayed action and lost momentum.",
  metric: "Immediate Answers",
  icon: MessageSquare
}, {
  id: "accountability",
  title: "Personal Ownership",
  blushTag: "Full Responsibility",
  consultantPoint: "My personal brand and professional reputation sit on every campaign. I intentionally cap client intake so I can remain fully dedicated to your success.",
  agencyPoint: "Your account is one of dozens managed by overworked team members facing high client churn.",
  metric: "Selective Intake",
  icon: ShieldCheck
}, {
  id: "modern-seo",
  title: "Modern & AI Search Ready",
  blushTag: "Next-Gen SEO Framework",
  consultantPoint: "I continuously adapt tactics for modern search algorithms, combining technical precision with AI Search Optimisation (LLMO) for long-term growth.",
  agencyPoint: "Slow to pivot away from outdated legacy playbooks, relying on generic AI content mills and vanity metrics.",
  metric: "Future-Proof Growth",
  icon: Zap
}];
const summaryPoints = [{
  id: 1,
  label: "Consultant Access",
  consultant: "Direct with Yassine Essaadi",
  agency: "Junior Account Manager"
}, {
  id: 2,
  label: "Strategy Format",
  consultant: "Bespoke to Revenue Goals",
  agency: "Template Deliverables"
}, {
  id: 3,
  label: "Retainer Allocation",
  consultant: "100% Hands-on SEO Work",
  agency: "Shared with Agency Margin"
}, {
  id: 4,
  label: "Turnaround Speed",
  consultant: "Agile & Direct Execution",
  agency: "Multi-layered Approval Loops"
}];
export default function ConsultantBenefits() {
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="aria-hidden:hidden pointer-events-none absolute -top-24 left-1/2 -z-10 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-secondary/50 blur-3xl opacity-60" />
      <div className="aria-hidden:hidden pointer-events-none absolute bottom-0 right-0 -z-10 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs uppercase tracking-wider font-semibold border border-accent/20 bg-secondary text-foreground rounded-full">
            Direct Senior Access
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            Why Work Directly With Me Instead of an Agency?
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you partner with me, you do not pay for agency account directors, sales teams, or lavish central offices. You get my direct hands-on SEO expertise, rapid execution, and a custom strategy focused solely on sustainable search growth for your business.
          </p>
        </motion.div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {benefitsList.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.08,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full border border-border/70 bg-card hover:border-accent/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-lg bg-secondary text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="bg-secondary/60 text-foreground border-accent/30 font-medium text-xs px-2.5 py-0.5 rounded-full">
                        <Sparkles className="size-3 text-accent mr-1 inline-block" />
                        {item.blushTag}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl font-semibold text-foreground font-serif">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-semibold text-primary uppercase tracking-wider mt-1">
                      {item.metric}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 space-y-4 flex-1 flex flex-col justify-between">
                    <div className="space-y-3">
                      {}
                      <div className="p-3.5 rounded-lg bg-muted/60 border border-border/40">
                        <div className="flex items-start gap-2.5">
                          <div className="mt-0.5 rounded-full bg-primary/10 text-primary p-0.5 shrink-0">
                            <Check className="size-3.5" />
                          </div>
                          <div>
                            <span className="text-xs font-semibold text-foreground block mb-0.5">
                              With Me (Yassine)
                            </span>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {item.consultantPoint}
                            </p>
                          </div>
                        </div>
                      </div>

                      {}
                      <div className="p-3 rounded-lg bg-background border border-border/30 opacity-75">
                        <div className="flex items-start gap-2.5">
                          <div className="mt-0.5 rounded-full bg-destructive/10 text-destructive p-0.5 shrink-0">
                            <X className="size-3.5" />
                          </div>
                          <div>
                            <span className="text-xs font-semibold text-muted-foreground block mb-0.5">
                              Traditional Agency
                            </span>
                            <p className="text-xs text-muted-foreground/80 leading-relaxed">
                              {item.agencyPoint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl bg-card border border-border shadow-sm p-6 sm:p-8 lg:p-10 mb-16">
          <div className="max-w-3xl mb-8">
            <h3 className="text-2xl font-bold font-serif text-foreground mb-2">
              Direct SEO Partnership at a Glance
            </h3>
            <p className="text-sm text-muted-foreground">
              Here is how working directly with an independent SEO consultant compares to signing a traditional agency retainer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {summaryPoints.map((row, idx) => <div key={row.id} data-index={idx} className="p-4 rounded-xl bg-background border border-border/60 flex flex-col justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  {row.label}
                </div>

                <div className="space-y-2">
                  <div className="p-2.5 rounded-md bg-secondary/50 border border-accent/20">
                    <span className="text-xs text-foreground font-medium block">
                      Freelance Consultant
                    </span>
                    <span className="text-xs font-semibold text-primary block mt-0.5">
                      {row.consultant}
                    </span>
                  </div>

                  <div className="p-2.5 rounded-md bg-muted/40 border border-border/40">
                    <span className="text-xs text-muted-foreground font-medium block">
                      SEO Agency
                    </span>
                    <span className="text-xs text-muted-foreground/80 block mt-0.5">
                      {row.agency}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 text-center relative overflow-hidden shadow-lg">
          {}
          <div className="aria-hidden:hidden pointer-events-none absolute top-0 right-0 h-64 w-64 translate-x-12 -translate-y-12 rounded-full bg-accent/20 blur-2xl" />

          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <Badge variant="outline" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 px-3 py-1 text-xs uppercase tracking-wider">
              Ready to Upgrade Your SEO?
            </Badge>

            <h3 className="text-2xl sm:text-3xl font-bold font-serif tracking-tight text-primary-foreground">
              Work Directly With Me on Your Search Strategy
            </h3>

            <p className="text-sm sm:text-base text-primary-foreground/90 leading-relaxed">
              No account managers. No template roadmaps. Book a free consultation today and speak directly with me about tailored organic growth for your business.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold px-8 py-6 rounded-lg w-full sm:w-auto">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground bg-primary/20 hover:bg-primary-foreground/10 font-medium px-6 py-6 rounded-lg w-full sm:w-auto">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}